import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  EyeOff, KeyRound, Loader2, Pencil, Plus, UserCheck, UserX,
} from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatDateID } from "../lib/format";
import { ConfirmDialog, tdClass, thClass } from "../components/Common";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const EMPTY_FORM = {
  name: "", username: "", email: "", password: "", confirm_password: "",
  role_id: "", status: "aktif", employee_id: "", notes: "",
};

const isOnlineUser = (u) =>
  u.last_seen && Date.now() - new Date(u.last_seen).getTime() < 5 * 60 * 1000;

const fmtAgo = (iso) => {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "baru saja";
  if (s < 3600) return `${Math.floor(s / 60)} mnt lalu`;
  if (s < 86400) return `${Math.floor(s / 3600)} jam lalu`;
  return `${Math.floor(s / 86400)} hari lalu`;
};

function RolePermPreview({ roleId, roles, groups, canSeeSpecial }) {
  const role = roles.find((r) => r.id === roleId);
  if (!role) return null;
  if (role.permissions.includes("*"))
    return canSeeSpecial ? (
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 flex items-start gap-2">
        <EyeOff className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-300">
          Role ini memiliki <span className="font-bold">SEMUA hak akses</span>, termasuk fitur
          tersembunyi <span className="font-bold">Pengeluaran Khusus</span>.
        </p>
      </div>
    ) : (
      <p className="text-xs text-slate-400 rounded-lg bg-[#0F172A] border border-slate-800 p-3">
        Role ini memiliki seluruh hak akses standar.
      </p>
    );
  const has = new Set(role.permissions);
  const visibleGroups = canSeeSpecial ? groups : groups.filter((g) => g.group !== "Pengeluaran Khusus");
  return (
    <div className="rounded-lg border border-slate-800 bg-[#0F172A] p-3 space-y-2 max-h-44 overflow-y-auto">
      {visibleGroups.map((g) => {
        const granted = g.items.filter((i) => has.has(i.key));
        if (!granted.length) return null;
        const isSpecial = g.group === "Pengeluaran Khusus";
        return (
          <div key={g.group}>
            <p className={`text-[10px] font-bold uppercase tracking-wider ${isSpecial ? "text-amber-600" : "text-slate-400"}`}>
              {g.group}{isSpecial ? " — fitur tersembunyi" : ""}
            </p>
            <div className="flex flex-wrap gap-1 mt-1">
              {granted.map((i) => (
                <span
                  key={i.key}
                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium border ${
                    isSpecial
                      ? "bg-amber-100 text-amber-300 border-amber-500/30"
                      : "bg-[#1E293B] text-slate-400 border-slate-800"
                  }`}
                >
                  {i.label}
                </span>
              ))}
            </div>
          </div>
        );
      })}
      {role.permissions.length === 0 && (
        <p className="text-xs text-slate-400">Role ini tidak memiliki hak akses apa pun.</p>
      )}
    </div>
  );
}

export default function Users() {
  const { user: me, hasPerm } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permGroups, setPermGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, target: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [statusTarget, setStatusTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get("/users"), api.get("/roles"), api.get("/permissions")])
      .then(([u, r, p]) => { setUsers(u.data); setRoles(r.data); setPermGroups(p.data); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const openAdd = () => {
    setForm({ ...EMPTY_FORM, role_id: roles.find((r) => r.slug === "viewer")?.id || roles[0]?.id || "" });
    setModal({ open: true, target: null });
  };

  const openEdit = (u) => {
    setForm({
      name: u.name, username: u.username, email: u.email, password: "", confirm_password: "",
      role_id: u.role_id || "", status: u.status, employee_id: u.employee_id || "", notes: u.notes || "",
    });
    setModal({ open: true, target: u });
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!modal.target) {
      if (!form.password || form.password.length < 8) {
        toast.error("Password minimal 8 karakter");
        return;
      }
      if (form.password !== form.confirm_password) {
        toast.error("Konfirmasi password tidak sama");
        return;
      }
    }
    setSaving(true);
    try {
      if (modal.target) {
        await api.put(`/users/${modal.target.id}`, {
          name: form.name, username: form.username, email: form.email,
          role_id: form.role_id, employee_id: form.employee_id, notes: form.notes,
        });
        toast.success("Pengguna diperbarui");
      } else {
        await api.post("/users", form);
        toast.success("Pengguna baru berhasil dibuat");
      }
      setModal({ open: false, target: null });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    setActionLoading(true);
    const next = statusTarget.status === "aktif" ? "nonaktif" : "aktif";
    try {
      await api.put(`/users/${statusTarget.id}/status`, { status: next });
      toast.success(next === "nonaktif" ? "Akun dinonaktifkan" : "Akun diaktifkan");
      setStatusTarget(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setActionLoading(false);
    }
  };

  const doReset = async () => {
    if (newPassword.length < 8) {
      toast.error("Password baru minimal 8 karakter");
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password ${resetTarget.name} berhasil direset`);
      setResetTarget(null);
      setNewPassword("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setActionLoading(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600";

  if (loading)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-100">Manajemen Pengguna</h2>
          <p className="text-xs text-slate-400">{users.length} akun terdaftar</p>
        </div>
        {hasPerm("create_users") && (
          <button
            data-testid="btn-add-user"
            onClick={openAdd}
            className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm shadow-sm"
          >
            <Plus className="w-4 h-4" /> Buat Akun Baru
          </button>
        )}
      </div>

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid="table-users" className="w-full border-collapse text-sm text-left">
            <thead>
              <tr>
                <th className={thClass}>Nama</th>
                <th className={thClass}>Username</th>
                <th className={thClass}>Role</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Online</th>
                <th className={thClass}>Last Login</th>
                <th className={`${thClass} text-center`}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-800/40" data-testid={`row-user-${u.id}`}>
                  <td className={tdClass}>
                    <p className="font-semibold text-slate-100">{u.name}</p>
                    <p className="text-xs text-slate-400">{u.email}</p>
                  </td>
                  <td className={`${tdClass} font-num text-xs`}>{u.username}</td>
                  <td className={tdClass}>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      u.role_label === "Super Admin" ? "bg-slate-900 text-white" : "bg-slate-800 text-slate-200"
                    }`}>{u.role_label}</span>
                  </td>
                  <td className={tdClass}>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      u.status === "aktif"
                        ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                        : "bg-slate-800 text-slate-400 border border-slate-800"
                    }`}>{u.status === "aktif" ? "Aktif" : "Nonaktif"}</span>
                  </td>
                  <td className={tdClass} data-testid={`online-status-${u.id}`}>
                    {isOnlineUser(u) ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400">
                        <span className="led-dot" style={{ width: 7, height: 7 }} /> Online
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">{u.last_seen ? fmtAgo(u.last_seen) : "—"}</span>
                    )}
                  </td>
                  <td className={`${tdClass} text-xs text-slate-400 whitespace-nowrap`}>
                    {u.last_login ? `${formatDateID(u.last_login.slice(0, 10))} ${u.last_login.slice(11, 16)}` : "Belum pernah"}
                  </td>
                  <td className={`${tdClass} text-center`}>
                    <div className="flex items-center justify-center gap-1">
                      {hasPerm("edit_users") && (
                        <button data-testid={`btn-edit-user-${u.id}`} title="Edit / Ubah Role" onClick={() => openEdit(u)} className="p-1.5 rounded-md hover:bg-slate-800/40 text-slate-400 hover:text-blue-300"><Pencil className="w-4 h-4" /></button>
                      )}
                      {hasPerm("reset_user_password") && (
                        <button data-testid={`btn-reset-user-${u.id}`} title="Reset Password" onClick={() => { setResetTarget(u); setNewPassword(""); }} className="p-1.5 rounded-md hover:bg-amber-500/10 text-slate-400 hover:text-amber-300"><KeyRound className="w-4 h-4" /></button>
                      )}
                      {hasPerm("deactivate_users") && u.id !== me?.id && (
                        <button
                          data-testid={`btn-toggle-user-${u.id}`}
                          title={u.status === "aktif" ? "Nonaktifkan" : "Aktifkan"}
                          onClick={() => setStatusTarget(u)}
                          className={`p-1.5 rounded-md ${u.status === "aktif" ? "hover:bg-rose-500/15 text-slate-400 hover:text-rose-400" : "hover:bg-emerald-500/15 text-slate-400 hover:text-emerald-400"}`}
                        >
                          {u.status === "aktif" ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={modal.open} onOpenChange={(o) => setModal((m) => ({ ...m, open: o }))}>
        <DialogContent data-testid="modal-user-form" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{modal.target ? `Edit Pengguna — ${modal.target.name}` : "Buat Akun Baru"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <Label>Nama Lengkap *</Label>
              <Input data-testid="input-user-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Username *</Label>
              <Input data-testid="input-user-username" value={form.username} onChange={(e) => set("username", e.target.value)} placeholder="huruf kecil, tanpa spasi" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Email *</Label>
              <Input data-testid="input-user-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            {!modal.target && (
              <>
                <div className="space-y-1.5">
                  <Label>Password * (min. 8 karakter)</Label>
                  <Input data-testid="input-user-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Konfirmasi Password *</Label>
                  <Input data-testid="input-user-confirm" type="password" value={form.confirm_password} onChange={(e) => set("confirm_password", e.target.value)} />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>Role *</Label>
              <select data-testid="select-user-role" className={inputCls} value={form.role_id} onChange={(e) => set("role_id", e.target.value)}>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
            <div className="col-span-2 space-y-1.5" data-testid="role-permission-preview">
              <Label>Hak Akses yang Didapat Akun Ini</Label>
              <RolePermPreview roleId={form.role_id} roles={roles} groups={permGroups} canSeeSpecial={hasPerm("view_special_expense")} />
              <p className="text-xs text-slate-400">
                Ingin mengatur haknya? Buka menu <span className="font-semibold">Role & Permission</span>.
              </p>
            </div>
            {!modal.target && (
              <div className="space-y-1.5">
                <Label>Status Akun</Label>
                <select data-testid="select-user-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                  <option value="aktif">Aktif</option>
                  <option value="nonaktif">Nonaktif</option>
                </select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Nomor/Identitas Pegawai</Label>
              <Input data-testid="input-user-employee-id" value={form.employee_id} onChange={(e) => set("employee_id", e.target.value)} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Keterangan</Label>
              <Input data-testid="input-user-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal((m) => ({ ...m, open: false }))}>Batal</Button>
            <Button data-testid="btn-user-save" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={save} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(resetTarget)} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent data-testid="modal-reset-password" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password — {resetTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Password Baru (min. 8 karakter)</Label>
            <Input data-testid="input-reset-password" type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            <p className="text-xs text-slate-400">Password lama tidak diperlukan. Sampaikan password baru ke pengguna.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Batal</Button>
            <Button data-testid="btn-reset-confirm" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={doReset} disabled={actionLoading}>
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(statusTarget)}
        onOpenChange={(o) => !o && setStatusTarget(null)}
        title={statusTarget?.status === "aktif" ? "Nonaktifkan Akun?" : "Aktifkan Akun?"}
        description={statusTarget ? `Akun "${statusTarget.name}" akan di${statusTarget.status === "aktif" ? "nonaktifkan dan tidak dapat login" : "aktifkan kembali"}.` : ""}
        onConfirm={toggleStatus}
        loading={actionLoading}
      />
    </div>
  );
}
